import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Users, User, PenTool, BarChart2, Plus, Menu, X, LogOut, Shield } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import NotificationsDropdown from './NotificationsDropdown';
import FeedbackLink from './FeedbackLink';
import IdentitySwitcher from './IdentitySwitcher';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [isIdentityOpen, setIsIdentityOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState<'reader' | 'author' | 'admin' | null>(null);
  const [currentIdentityId, setCurrentIdentityId] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchUserRole();
      const storedIdentityId = localStorage.getItem('current_identity_id');
      setCurrentIdentityId(storedIdentityId || user.id);
    }
  }, [user]);

  const fetchUserRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user!.id)
        .single();
      
      setUserRole(profile?.role || null);
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };

  const isAuthorOrAdmin = userRole === 'author' || userRole === 'admin';

  const getNavItems = () => {
    if (!user) {
      return [];
    }

    const items = [
      {
        icon: BookOpen,
        label: 'Book Ideas',
        path: '/',
        active: location.pathname === '/'
      },
      {
        icon: Users,
        label: 'Beta Read',
        path: '/beta/reader',
        active: location.pathname.startsWith('/beta')
      }
    ];

    if (isAuthorOrAdmin) {
      items.push(
        {
          icon: PenTool,
          label: 'Author Dashboard',
          path: '/dashboard',
          active: location.pathname === '/dashboard'
        },
      
      );
    }

    // Add Admin Dashboard link for admin users
    if (userRole === 'admin') {
      items.push({
        icon: Shield,
        label: 'Admin Dashboard',
        path: '/admin',
        active: location.pathname === '/admin'
      });
    }

    // Use currentIdentityId for the profile link
    const profilePath = currentIdentityId === user.id ? 
      `/profile/${currentIdentityId}` : 
      `/pen-name/${currentIdentityId}`;

    items.push({
      icon: User,
      label: 'My Profile',
      path: profilePath,
      active: location.pathname === profilePath
    });

    return items;
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // If user is not authenticated, render only the content without the navigation
  if (!user) {
    return (
      <div className="min-h-screen bg-[#faf5ff]">
        <main className="min-h-screen">
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#faf5ff]">
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 bg-white rounded-lg shadow-md"
        >
          {isMobileMenuOpen ? (
            <X className="w-6 h-6 text-purple-600" />
          ) : (
            <Menu className="w-6 h-6 text-purple-600" />
          )}
        </button>
      </div>

      {/* Overlay for mobile */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Left Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-white shadow-lg z-50 transition-transform duration-300 lg:translate-x-0 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="p-6 border-b">
          <Link to="/" className="flex items-center gap-2" onClick={closeMobileMenu}>
            <BookOpen className="w-8 h-8 text-purple-600" />
            <span className="font-bold text-xl text-gray-900">StoryMuse</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {getNavItems().map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileMenu}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                item.active
                  ? 'bg-purple-50 text-purple-700'
                  : 'text-gray-600 hover:bg-purple-50 hover:text-purple-700'
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </Link>
          ))}

          {/* Feedback Link - Moved here */}
          <div className="px-4 py-3">
            <FeedbackLink />
          </div>

          <button
            onClick={signOut}
            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors w-full text-left text-gray-600 hover:bg-red-50 hover:text-red-700"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </nav>

        {/* New Book Idea Button - Only show for authors/admins */}
        {isAuthorOrAdmin && (
          <div className="px-4 mt-6">
            <Link
              to="/new-idea"
              onClick={closeMobileMenu}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              New Book Idea
            </Link>
          </div>
        )}

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
          <div className="flex items-center justify-between">
            <IdentitySwitcher 
              isOpen={isIdentityOpen}
              onToggle={() => setIsIdentityOpen(!isIdentityOpen)}
              position="top"
            />
            <div className="flex items-center gap-2">
              <NotificationsDropdown position="top" />
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}